import React, { useState, useEffect } from "react";
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { useSearchParams } from "react-router-dom";
import { db } from "../../../firebase"; // Adjust the path as needed
import {
  TextField,
  Button,
  Grid,
  Container,
  Typography,
  Card,
  CardContent,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  IconButton,
  FormControl,
  InputLabel,
  Box,
  Select,
  MenuItem,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import DynamicUploadModal from "../../../components/UploadModal";
import ClinicSelector from "../../../components/topicCategorySelector/ClinicSelector";

// Example JSON data
const categoriesData = [
  {
    name: "Pathology",
    subcategories: [
      {
        name: "Blood Tests",
        tests: ["Complete Blood Count (CBC)", "Blood Sugar", "Lipid Profile"],
      },
      {
        name: "Urine Tests",
        tests: ["Urinalysis", "Urine Culture"],
      },
      {
        name: "Tissue Analysis",
        tests: ["Biopsy", "Cytology"],
      },
    ],
  },
  {
    name: "Radiology",
    subcategories: [
      { name: "X-ray", tests: [] },
      { name: "MRI", tests: [] },
      { name: "CT Scan", tests: [] },
    ],
  },
  {
    name: "Cardiology",
    subcategories: [
      { name: "ECG", tests: [] },
      { name: "Echo", tests: [] },
      { name: "Stress Test", tests: [] },
    ],
  },
];

const AddLabTest = () => {
  const [openModal, setOpenModal] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(""); // State to store the uploaded URL
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  const [searchParams] = useSearchParams();
  const uid = searchParams.get("uid");

  const handleModalClose = () => {
    setOpenModal(false);
  };

  useEffect(() => {
    const fetchLabTest = async () => {
      if (uid) {
        const docRef = doc(db, "labtest", uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setLabTest(docSnap.data());
        } else {
          console.error("No such document!");
        }
      }
    };

    fetchLabTest();
  }, [uid]);

  const handleUploadSuccess = (url) => {
    console.log("Image uploaded successfully! URL:", url);
    setUploadedImageUrl(url); // Set the uploaded URL to the state
    setLabTest((prevData) => ({
      ...prevData,
      imageUrl: url, // Set the uploaded URL in the labTest
    }));
  };

  const [labTest, setLabTest] = useState({
    availability: false,
    category: "",
    sub_category: "",
    timestamp: "",
    critical_value: 0,
    description: "",
    imageUrl: "",
    instructions: "",
    is_home_test: false,
    normal_range: "",
    preparation: "",
    price: 0, // Set to number type
    report_format: "",
    result_units: "",
    risk_factors: "",
    sample_type: "",
    test_duration: "",
    test_frequency: "",
    clinicname: "",
    clinic_id: "",
    test_id: "",
    test_location: "",
    test_method: "",
    test_name: "",
    test_technology: "",
    updated_at: "", // This will be set to server timestamp
  });

  const [openSnackbar, setOpenSnackbar] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLabTest((prevTest) => ({
      ...prevTest,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
          ? Number(value)
          : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const labTestData = {
      ...labTest,
      updated_at: serverTimestamp(),
    };

    try {
      if (uid) {
        // Update existing document
        const docRef = doc(db, "labtest", uid);
        await updateDoc(docRef, labTestData);
      } else {
        // Create a new document
        const uniqueId = uuidv4();
        await setDoc(doc(db, "labtest", uniqueId), {
          ...labTestData,
          test_id: uniqueId,
          timestamp: serverTimestamp(),
        });
      }

      setOpenSnackbar(true);
    } catch (error) {
      console.error("Error saving lab test: ", error);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleSelectClinic = (selectedClinic) => {
    setLabTest((prevData) => ({
      ...prevData,
      clinicname: selectedClinic.name, // Update the specialist field
      clinic_id: selectedClinic.id, // Update the specialist field
    }));
  };

  const handleCategoryChange = (event) => {
    const categoryName = event.target.value;
    const category = categoriesData.find((c) => c.name === categoryName);
    setSelectedCategory(categoryName);
    setSelectedSubcategory(null);
    setLabTest((prevData) => ({
      ...prevData,
      category: categoryName,
      sub_category: "", // Reset subcategory
    }));
  };

  const handleSubcategoryChange = (event) => {
    const subcategoryName = event.target.value;
    setSelectedSubcategory(subcategoryName);
    setLabTest((prevData) => ({
      ...prevData,
      sub_category: subcategoryName,
    }));
  };

  const currentCategory = categoriesData.find(
    (category) => category.name === selectedCategory
  );

  const currentSubcategory =
    currentCategory?.subcategories.find(
      (subcategory) => subcategory.name === selectedSubcategory
    ) || null;
  return (
    <Container
      maxWidth="sm"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <Card>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom>
          {uid ? "Update Lab Test" : "Add Lab Test"}
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Test Name"
                  name="test_name"
                  value={labTest.test_name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={labTest.description}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                {/* Category Spinner */}
                <FormControl sx={{ minWidth: 200, mr: 2 }}>
                  <InputLabel id="category-label">Select Category</InputLabel>
                  <Select
                    labelId="category-label"
                    value={labTest.category}
                    onChange={handleCategoryChange}
                    label="Select Category"
                    required
                  >
                    {categoriesData.map((category, index) => (
                      <MenuItem key={index} value={category.name}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Subcategory Spinner */}
                {/* Subcategory Spinner */}
                {(selectedCategory || labTest.category) && (
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel id="subcategory-label">
                      Select Subcategory
                    </InputLabel>
                    <Select
                      labelId="subcategory-label"
                      value={labTest.sub_category}
                      onChange={handleSubcategoryChange}
                      label="Select Subcategory"
                      required
                    >
                      <MenuItem
                            value={labTest.sub_category}
                          >
                            {labTest.sub_category}
                          </MenuItem>
                      {currentCategory?.subcategories.map(
                        (subcategory, index) => (
                          <MenuItem
                            key={index}
                            value={subcategory.name || "hell"}
                          >
                            {subcategory.name || "hell"}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Image URL"
                  name="imageUrl"
                  value={labTest.imageUrl}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <IconButton
                  onClick={() => setOpenModal(true)}
                  color="primary"
                  aria-label="upload image"
                >
                  <PhotoCamera />
                </IconButton>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Price"
                  name="price"
                  type="number"
                  value={labTest.price}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Critical Value"
                  name="critical_value"
                  type="number"
                  value={labTest.critical_value}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Normal Range"
                  name="normal_range" // Consider using a more complex input for maps
                  value={labTest.normal_range} // Convert map to string for input
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Instructions"
                  name="instructions"
                  value={labTest.instructions}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Preparation"
                  name="preparation"
                  value={labTest.preparation}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Test Duration"
                  name="test_duration"
                  value={labTest.test_duration}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Test Frequency"
                  name="test_frequency"
                  value={labTest.test_frequency}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Sample Type"
                  name="sample_type"
                  value={labTest.sample_type}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <ClinicSelector onSelectClinic={handleSelectClinic} />
                  <Box sx={{ height: 16 }} /> {/* Adds a gap of 16px */}
                </Box>
                <TextField
                  label="Clinic ID"
                  name="clinic_id"
                  value={labTest.clinic_id}
                  onChange={handleChange}
                  disabled
                  fullWidth
                  required
                  sx={{ marginBottom: 2 }}
                />
                <TextField
                  label="Clinic Name"
                  name="clinicname"
                  value={labTest.clinicname}
                  onChange={handleChange}
                  fullWidth
                  disabled
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Test Location"
                  name="test_location"
                  value={labTest.test_location}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Test Method"
                  name="test_method"
                  value={labTest.test_method}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Result Units"
                  name="result_units"
                  value={labTest.result_units}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Risk Factors"
                  name="risk_factors"
                  value={labTest.risk_factors}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Report Format"
                  name="report_format"
                  value={labTest.report_format}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Test Technology"
                  name="test_technology"
                  value={labTest.test_technology}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={labTest.availability}
                      onChange={handleChange}
                      name="availability"
                    />
                  }
                  label="Available"
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  {uid ? "Update Lab Test" : "Add Lab Test"}
                </Button>
              </Grid>
            </Grid>
          </form>
          <DynamicUploadModal
            open={openModal}
            onClose={handleModalClose}
            uploadPreset="bookMyDr" // Custom Cloudinary upload preset
            folderName="Madecare" // Custom folder name
            maxSize={5 * 1024 * 1024} // Custom max file size: 5MB
            onUploadSuccess={handleUploadSuccess} // Callback after success
          />
        </CardContent>
      </Card>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          Lab Test added successfully!
        </Alert>
      </Snackbar>
      {/* Upload Modal */}
      <DynamicUploadModal
        open={openModal}
        onClose={handleModalClose}
        uploadPreset="bookMyDr" // Custom Cloudinary upload preset
        folderName="Madecare" // Custom folder name
        maxSize={5 * 1024 * 1024} // Custom max file size: 5MB
        onUploadSuccess={handleUploadSuccess} // Callback after success
      />
    </Container>
  );
};

export default AddLabTest;
